import type { MaybeRefOrGetter } from "vue";
import { v7 as uuidv7 } from "uuid";
import { formatMapboxLocationName } from "../utils/location";

// Recherche de lieux partagée web/mobile via la Search Box API Mapbox (suggest / retrieve).
// Le consentement Mapbox est INJECTÉ (option `mapboxAllowed`) : web → cookie-consent (cf.
// l'enveloppe useMapboxSearch), mobile/natif → toujours autorisé (défaut true). Même découplage
// que AppMap (prop `mapboxAllowed`).

export interface MapboxSuggestion {
  mapbox_id: string;
  name: string;
  full_address?: string;
  place_formatted?: string;
  feature_type: string;
  poi_category_ids?: string[];
  context?: {
    country?: { name?: string };
    region?: { name?: string };
    place?: { name?: string };
  };
}

export interface MapboxRetrieved {
  center: [number, number];
  bbox?: [number, number, number, number];
  name: string;
  placeFormatted: string;
  // Properties complètes du feature Mapbox retrieve (métadonnées du lieu sélectionné).
  properties?: Record<string, unknown>;
}

// Catégories de lieux acceptées par le paramètre `types` de la Search Box API Mapbox.
export type MapboxSearchType = "country" | "region" | "postcode" | "district" | "place" | "city" | "locality" | "neighborhood" | "street" | "address" | "poi";

// Filtrage POI par EXCLUSION (`poi_category_exclusions`) plutôt que par whitelist (`poi_category`) :
// un POI sans catégorie — fréquent pour plages, lacs et points de vue — n'appartient à aucune
// catégorie bannie et reste donc renvoyé. On exclut les catégories parentes hors-sujet, ce qui
// conserve implicitement outdoors/park/tourist_attraction/sports (plages, piscines, réserves,
// points de vue, clubs) ainsi que tout POI non catégorisé. N'affecte que les POI.
export const EXCLUDED_POI_CATEGORIES: string[] = ["food", "food_and_drink", "shopping", "services", "health_services", "financial_services", "lodging", "apartment_or_condo", "transportation", "education", "place_of_worship", "entertainment", "nightlife", "office", "government", "gas_station"];

export interface UseMapboxSearchOptions {
  types?: MaybeRefOrGetter<MapboxSearchType[] | undefined>;
  poiCategoryExclusions?: MaybeRefOrGetter<string[] | undefined>;
  language?: MaybeRefOrGetter<string | undefined>;
  limit?: MaybeRefOrGetter<number | undefined>;
  debounceMs?: number;
  proximity?: "ip" | (() => [number, number] | undefined);
  // Consentement Mapbox injecté par l'hôte (défaut : autorisé).
  mapboxAllowed?: MaybeRefOrGetter<boolean>;
  boostBroadFeatures?: MaybeRefOrGetter<boolean>;
}

const BROAD_FEATURE_TYPES: MapboxSearchType[] = ["country", "region"];

// Vivier maximal pour le filtrage/re-tri ; l'option `limit` ne borne QUE l'affichage final.
const MAPBOX_SUGGEST_REQUEST_LIMIT = 10;

const FEATURE_TYPE_RANK: Record<string, number> = {
  country: 0,
  region: 1,
  district: 2,
  place: 3,
  city: 3,
  locality: 4,
  neighborhood: 5,
  postcode: 6,
  street: 7,
  address: 8,
  poi: 9,
};

function normalizeForMatch(value: string): string {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

function matchesWholeWord(name: string, term: string): boolean {
  const normalizedTerm = normalizeForMatch(term).trim();
  if (!normalizedTerm) return false;
  const escaped = normalizedTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}([^\\p{L}\\p{N}]|$)`, "u").test(normalizeForMatch(name));
}

const SEASONAL_RENTAL_NAME_PATTERN = /\b(apartment|appartement|bedroom|studio|gite|mobil[- ]?home|cottage|guest ?house|b&b|bnb|duplex|logement|meuble|homestay|housing|holiday|villa|suite|loft|t[1-5]|f[1-5])\b/;

// Mapbox indexe les locations Airbnb en POI SANS catégorie, comme les vrais spots : seul le nom les distingue.
function isSeasonalRentalNoise(suggestion: MapboxSuggestion): boolean {
  if (suggestion.feature_type !== "poi") return false;
  if (suggestion.poi_category_ids?.length) return false;
  return SEASONAL_RENTAL_NAME_PATTERN.test(normalizeForMatch(suggestion.name));
}

function mergeSuggestions(primary: MapboxSuggestion[], broad: MapboxSuggestion[]): MapboxSuggestion[] {
  const seen = new Set<string>();
  const merged: MapboxSuggestion[] = [];
  for (const suggestion of [...primary, ...broad]) {
    if (seen.has(suggestion.mapbox_id)) continue;
    seen.add(suggestion.mapbox_id);
    merged.push(suggestion);
  }
  return merged;
}

// Mapbox n'expose aucun tri personnalisable : re-tri local (terme en mot entier du nom, puis entité la plus large).
function rankSuggestions(list: MapboxSuggestion[], term: string): MapboxSuggestion[] {
  return list
    .map((suggestion, index) => ({
      suggestion,
      index,
      exact: matchesWholeWord(suggestion.name, term) ? 0 : 1,
      typeRank: FEATURE_TYPE_RANK[suggestion.feature_type] ?? 10,
    }))
    .sort((a, b) => a.exact - b.exact || (a.exact === 0 ? a.typeRank - b.typeRank : 0) || a.index - b.index)
    .map((entry) => entry.suggestion);
}

export function useAppMapboxSearch(options: UseMapboxSearchOptions = {}) {
  // Options résolues paresseusement (toValue) à chaque requête : un changement de prop
  // côté appelant — ou un hot-reload — est ainsi pris en compte sans re-setup du composable.
  const DEFAULT_TYPES: MapboxSearchType[] = ["country", "region", "place", "city", "locality", "neighborhood", "street", "address", "poi"];
  const resolveTypes = (): MapboxSearchType[] => {
    const value = toValue(options.types);
    return value && value.length ? value : DEFAULT_TYPES;
  };
  const resolveLanguage = (): string => toValue(options.language) ?? appLocale();
  const resolveLimit = (): number => toValue(options.limit) ?? 10;
  const resolvePoiCategoryExclusions = (): string[] => {
    const value = toValue(options.poiCategoryExclusions);
    return value && value.length ? value : EXCLUDED_POI_CATEGORIES;
  };
  const isAllowed = (): boolean => toValue(options.mapboxAllowed) ?? true;
  const resolveBoost = (): boolean => toValue(options.boostBroadFeatures) ?? false;
  const debounceMs = options.debounceMs ?? 250;
  const proximity = options.proximity;

  const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string;
  const baseUrl = "https://api.mapbox.com/search/searchbox/v1";

  const suggestions = ref<MapboxSuggestion[]>([]);
  const loading = ref(false);
  const error = ref<Error | null>(null);

  let sessionToken = newSessionToken();
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let abortController: AbortController | undefined;

  function newSessionToken(): string {
    return uuidv7();
  }

  async function requestSuggestions(q: string, signal: AbortSignal, typesOverride?: MapboxSearchType[]): Promise<MapboxSuggestion[]> {
    const url = new URL(`${baseUrl}/suggest`);
    url.searchParams.set("q", q);
    url.searchParams.set("access_token", accessToken);
    url.searchParams.set("session_token", sessionToken);
    url.searchParams.set("language", resolveLanguage());
    url.searchParams.set("limit", String(MAPBOX_SUGGEST_REQUEST_LIMIT));
    const types = typesOverride ?? resolveTypes();
    if (types.length) url.searchParams.set("types", types.join(","));
    const poiCategoryExclusions = resolvePoiCategoryExclusions();
    if (poiCategoryExclusions.length) url.searchParams.set("poi_category_exclusions", poiCategoryExclusions.join(","));
    if (proximity === "ip") {
      url.searchParams.set("proximity", "ip");
    } else if (typeof proximity === "function") {
      const p = proximity();
      if (p) url.searchParams.set("proximity", `${p[0]},${p[1]}`);
    }

    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`Mapbox /suggest HTTP ${res.status}`);
    const json = (await res.json()) as { suggestions?: MapboxSuggestion[] };
    return (json.suggestions ?? []).filter((suggestion) => !isSeasonalRentalNoise(suggestion));
  }

  async function fetchSuggestions(q: string) {
    if (!q || q.trim().length < 2) {
      suggestions.value = [];
      return;
    }
    if (!isAllowed()) {
      suggestions.value = [];
      return;
    }

    abortController?.abort();
    abortController = new AbortController();
    const { signal } = abortController;

    loading.value = true;
    error.value = null;
    try {
      if (resolveBoost()) {
        // /suggest successifs au même session_token = une seule session Mapbox facturée (un token réutilisé en concurrent facturerait de façon imprévisible).
        const primary = await requestSuggestions(q, signal);
        const broad = (
          await requestSuggestions(q, signal, BROAD_FEATURE_TYPES).catch((err) => {
            if ((err as Error).name === "AbortError") throw err;
            return [] as MapboxSuggestion[];
          })
        ).filter((suggestion) => matchesWholeWord(suggestion.name, q));
        suggestions.value = rankSuggestions(mergeSuggestions(primary, broad), q).slice(0, resolveLimit());
      } else {
        suggestions.value = (await requestSuggestions(q, signal)).slice(0, resolveLimit());
      }
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      error.value = e as Error;
      suggestions.value = [];
    } finally {
      loading.value = false;
    }
  }

  function search(q: string) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => fetchSuggestions(q), debounceMs);
  }

  async function retrieve(suggestion: MapboxSuggestion): Promise<MapboxRetrieved | undefined> {
    if (!isAllowed()) return undefined;
    try {
      const url = new URL(`${baseUrl}/retrieve/${suggestion.mapbox_id}`);
      url.searchParams.set("access_token", accessToken);
      url.searchParams.set("session_token", sessionToken);
      url.searchParams.set("language", resolveLanguage());

      const res = await fetch(url);
      if (!res.ok) throw new Error(`Mapbox /retrieve HTTP ${res.status}`);
      const json = (await res.json()) as {
        features?: Array<{
          geometry?: { coordinates?: [number, number] };
          properties?: Record<string, unknown> & {
            name?: string;
            place_formatted?: string;
            bbox?: [number, number, number, number];
          };
        }>;
      };
      const feat = json.features?.[0];
      const coords = feat?.geometry?.coordinates;
      if (!feat || !coords) return undefined;

      sessionToken = newSessionToken();

      const baseName = feat.properties?.name ?? suggestion.name;
      return {
        center: coords,
        bbox: feat.properties?.bbox,
        name: formatMapboxLocationName(feat.properties, baseName),
        placeFormatted: feat.properties?.place_formatted ?? suggestion.place_formatted ?? "",
        properties: feat.properties,
      };
    } catch (e) {
      error.value = e as Error;
      return undefined;
    }
  }

  function clear() {
    clearTimeout(debounceTimer);
    abortController?.abort();
    suggestions.value = [];
    loading.value = false;
    error.value = null;
  }

  return { suggestions, loading, error, search, retrieve, clear };
}
